import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { InteractiveTankPlan } from "@/components/chem/InteractiveTankPlan";
import { ChemPageShell, ChemSectionBlock } from "@/pages/shared/ChemCarriageShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CHEM_VESSELS, CHEM_SAMPLES, CHEM_HSE_KPIS, CHEM_VETTING } from "@/data/chemCarriageData";
import {
  Ship, Beaker, FlaskConical, Layers, Wind, Thermometer, Activity, QrCode,
  ShieldCheck, Award, AlertTriangle, FileText, ScrollText, Trash2, Gauge,
  TrendingUp, Briefcase, Leaf, Calendar, FileBox, BarChart3, Download, CheckCircle2,
  Anchor, MapPin, Clock
} from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { id: "ga", label: "General Arrangement" },
  { id: "fleet", label: "Fleet Cargo Status" },
  { id: "ledger", label: "Tank History (L3C)" },
  { id: "compat", label: "Compatibility Engine" },
  { id: "cleaning", label: "Tank Cleaning Ops" },
  { id: "coating", label: "Coating Passport" },
  { id: "igs", label: "IGS & N2 Systems" },
  { id: "heating", label: "Cargo Heating" },
  { id: "telemetry", label: "Live Telemetry" },
  { id: "samples", label: "Sampling & Custody" },
  { id: "vetting", label: "Vetting Hub" },
  { id: "crew", label: "Crew Endorsements" },
  { id: "drills", label: "Drills & Exercises" },
  { id: "marpol", label: "MARPOL & Slop" },
  { id: "incidents", label: "Incident Register" },
  { id: "performance", label: "CP Performance" },
  { id: "cp", label: "Charter Party Library" },
  { id: "demurrage", label: "Demurrage Workbench" },
  { id: "supercargo", label: "Supercargo Workflow" },
  { id: "sustain", label: "Sustainability" },
  { id: "survey", label: "Survey Look-Ahead" },
  { id: "vault", label: "Document Vault & Q88" },
  { id: "kpi", label: "Audit & KPI" },
];

const StatusDot = ({ s }: { s: string }) => {
  const map: Record<string,string> = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[s] || "bg-muted"}`} />;
};

const VesselCard = ({ v }: { v: any }) => (
  <Card className="hover:shadow-md transition">
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-display text-base font-bold">{v.name}</p>
          <p className="text-[11px] text-muted-foreground">IMO {v.imo} · {v.flag}</p>
        </div>
        <Badge variant="outline" className="text-[10px]">{v.status}</Badge>
      </div>
      <div className="text-xs text-muted-foreground space-y-1 mb-3">
        <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{v.location.port}</div>
        <div className="flex items-center gap-1.5"><Anchor className="w-3 h-3" />Charterer: <span className="text-foreground">{v.charterer}</span></div>
        <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" />Next: {v.nextPort} · ETA {v.eta}</div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3 pt-3 border-t border-border">
        {Object.entries(v.cargoSystem).map(([k, s]) => (
          <div key={k} className="text-center">
            <StatusDot s={s as string} />
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">{k}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground pt-3 border-t border-border">
        <span>CDI-M: {v.daysSinceCDIM}d ago</span>
        <span>OVMSA2: {v.daysSinceOVMSA}d ago</span>
      </div>
    </CardContent>
  </Card>
);

const EmployeeChemCarriage = () => {
  const [selectedVessel, setSelectedVessel] = useState(CHEM_VESSELS[0].name);
  const v = CHEM_VESSELS.find(x => x.name === selectedVessel)!;

  return (
    <IntranetLayout>
      <ChemPageShell
        title="Chem Carriage"
        subtitle="Operational hub for the Velocity Offshore chemical PSV fleet — dual-vetted under OVMSA2 and CDI-M, operated to chemical-tanker standards."
        sections={SECTIONS}
      >
        {/* 1 General Arrangement */}
        <ChemSectionBlock id="ga" n={1} title="General Arrangement" blurb="Interactive double-hull tank plan with live ullage, IGS and coating overlays — synced to admin-edited cargo levels.">
          <div className="flex items-center gap-2 mb-3">
            <p className="text-[11px] font-bold tracking-[2px] uppercase text-teal">Vessel</p>
            <Select value={selectedVessel} onValueChange={setSelectedVessel}>
              <SelectTrigger className="h-8 text-xs w-72"><SelectValue /></SelectTrigger>
              <SelectContent>{CHEM_VESSELS.map(x => <SelectItem key={x.imo} value={x.name}>{x.name} ({x.type})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <InteractiveTankPlan vessel={v} />
        </ChemSectionBlock>

        {/* 2 Fleet */}
        <ChemSectionBlock id="fleet" n={2} title="Fleet Cargo Status" blurb="Single-pane view of every chem PSV and what each is carrying.">

          <div className="flex flex-wrap gap-2 mb-2">
            <Badge variant="outline">All vessels</Badge>
            <Badge variant="outline">All charterers</Badge>
            <Badge variant="outline">Annex II: any</Badge>
            <Button onClick={() => toast.success("Created: Add Voyage", { description: "New entry added." })} size="sm" className="ml-auto" variant="outline"><Ship className="w-3 h-3 mr-1" />Add Voyage</Button>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {CHEM_VESSELS.map(v => <VesselCard key={v.imo} v={v} />)}
          </div>
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-bold tracking-[2px] uppercase text-teal">Interactive Tank Plan</p>
              <Select value={selectedVessel} onValueChange={setSelectedVessel}>
                <SelectTrigger className="h-8 text-xs w-72"><SelectValue /></SelectTrigger>
                <SelectContent>{CHEM_VESSELS.map(x => <SelectItem key={x.imo} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <InteractiveTankPlan vessel={v} />
          </div>
        </ChemSectionBlock>

        {/* 2 Ledger */}
        <ChemSectionBlock id="ledger" n={2} title="Tank History Ledger — Last 3 Cargoes" blurb="Auditable cargo provenance per tank. Append-only.">
          <Select value={selectedVessel} onValueChange={setSelectedVessel}>
            <SelectTrigger className="w-72"><SelectValue /></SelectTrigger>
            <SelectContent>{CHEM_VESSELS.map(v => <SelectItem key={v.imo} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="space-y-2">
            {v.tanks.map(t => (
              <Card key={t.no}><CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-bold text-sm">Tank {t.no}</p>
                    <p className="text-[11px] text-muted-foreground">{t.capacity} m³ · {t.coating}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{t.status}</Badge>
                    <Button onClick={() => toast.success("Generating: L3C PDF", { description: "Your file will download shortly." })} size="sm" variant="outline" className="text-xs"><Download className="w-3 h-3 mr-1" />L3C PDF</Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {t.l3c.map((c, i) => (
                    <div key={i} className="rounded-md bg-muted/50 p-2">
                      <p className="text-[9px] uppercase text-muted-foreground">Cargo {i+1}</p>
                      <p className="font-medium">{c}</p>
                      <p className="text-[10px] text-muted-foreground">Wall-wash: pass</p>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            ))}
          </div>
        </ChemSectionBlock>

        {/* 3 Compatibility */}
        <ChemSectionBlock id="compat" n={3} title="Cargo Compatibility Engine" blurb="Pre-qualify cargoes against tank coating and previous cargo before fixture commitment.">
          <Card><CardContent className="p-5 grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Input placeholder="Cargo (e.g. methanol)" />
              <Input placeholder="UN Number" />
              <Input placeholder="Quantity (MT)" />
              <Input placeholder="Loading Temp (°C)" />
              <Button onClick={() => toast.success("Action recorded: Run Compatibility")} className="w-full"><FlaskConical className="w-4 h-4 mr-1" />Run Compatibility</Button>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:bg-emerald-950/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <p className="font-bold text-sm text-emerald-900 dark:text-emerald-200">Compatible</p>
              </div>
              <ul className="text-xs space-y-1 text-foreground/80">
                <li>✓ Coating compatibility (Zn Silicate / Methanol)</li>
                <li>✓ USCG 46 CFR 150 cleared</li>
                <li>⚙ Pre-clean: hot wash 60°C × 4 hr, drying 6 hr</li>
                <li>⏱ Estimated turnaround: 14 hours</li>
              </ul>
              <Button onClick={() => toast.success("Created: Generate Pre-Loading Plan", { description: "New entry added." })} size="sm" variant="outline" className="mt-3"><FileText className="w-3 h-3 mr-1" />Generate Pre-Loading Plan</Button>
            </div>
          </CardContent></Card>
        </ChemSectionBlock>

        {/* 4 Cleaning */}
        <ChemSectionBlock id="cleaning" n={4} title="Tank Cleaning Operations" blurb="Kanban board of active cleaning jobs and historical library.">
          <Tabs defaultValue="active">
            <TabsList><TabsTrigger value="active">Active Jobs</TabsTrigger><TabsTrigger value="library">Cleaning Library</TabsTrigger></TabsList>
            <TabsContent value="active" className="mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["Pre-Wash","Main Wash","Drying","Wall-Wash Test"].map(stage => (
                  <Card key={stage}><CardContent className="p-3">
                    <p className="text-[10px] font-bold uppercase text-muted-foreground mb-2">{stage}</p>
                    <div className="rounded-md bg-muted p-2 text-xs">
                      <p className="font-medium">Velocity Borealis · T3P</p>
                      <p className="text-muted-foreground text-[10px]">Base Oil → Methanol</p>
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="library" className="mt-4 text-xs text-muted-foreground">Searchable archive of every historical cleaning job — vessel, tank, prev/next cargo, date.</TabsContent>
          </Tabs>
        </ChemSectionBlock>

        {/* 5 Coating */}
        <ChemSectionBlock id="coating" n={5} title="Coating System Passport" blurb="Per-tank coating lifecycle tracking with health heatmap.">
          <div className="grid md:grid-cols-3 gap-3">
            {CHEM_VESSELS.map(v => (
              <Card key={v.imo}><CardContent className="p-4">
                <p className="font-bold text-sm">{v.name}</p>
                <p className="text-[11px] text-muted-foreground mb-2">{v.coatingType}</p>
                <div className="flex gap-1">
                  {Array.from({length: v.tankCount}).map((_,i) => (
                    <span key={i} className={`flex-1 h-3 rounded-sm ${i < 1 ? "bg-amber-500" : "bg-emerald-500"}`} title={`T${i+1}`} />
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">{v.tankCount-1} green · 1 amber · next inspection in 14d</p>
              </CardContent></Card>
            ))}
          </div>
        </ChemSectionBlock>

        {/* 6 IGS */}
        <ChemSectionBlock id="igs" n={6} title="Inert Gas & N2 Systems" blurb="Live readings, generation rate, and consumable tracking.">
          <div className="grid md:grid-cols-3 gap-3">
            {[
              { l: "O₂ Content", v: "1.8%", t: "Target <5%" },
              { l: "N2 Purity", v: "99.4%", t: "Membrane PSA" },
              { l: "Generation Rate", v: "180 Nm³/h", t: "Steady" },
            ].map(x => (
              <Card key={x.l}><CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{x.l}</p>
                <p className="font-display text-2xl font-bold">{x.v}</p>
                <p className="text-[10px] text-muted-foreground">{x.t}</p>
              </CardContent></Card>
            ))}
          </div>
        </ChemSectionBlock>

        {/* 7 Heating */}
        <ChemSectionBlock id="heating" n={7} title="Cargo Heating & Cooling" blurb="Heating coil performance and bunker-burn estimator.">
          <Card><CardContent className="p-4 text-xs text-muted-foreground">
            Steam coils · max 90°C · last pressure test 2026-02-14 · current heating: T1P MEG 38°C → 45°C ETA 6h, est burn 1.4 t MGO.
          </CardContent></Card>
        </ChemSectionBlock>

        {/* 8 Telemetry */}
        <ChemSectionBlock id="telemetry" n={8} title="Live Cargo Telemetry" blurb="Real-time tank dashboard from cargo control system.">
          <Card><CardContent className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {v.tanks.map(t => (
                <div key={t.no} className="rounded-lg border border-border p-3">
                  <p className="font-bold text-sm">T{t.no}</p>
                  <p className="text-[10px] text-muted-foreground">Ullage 1.42 m</p>
                  <p className="text-[10px] text-muted-foreground">Temp 32.1°C</p>
                  <p className="text-[10px] text-muted-foreground">Pressure +120 mbar</p>
                  <div className="flex gap-1 mt-2"><StatusDot s="green" /><span className="text-[9px]">IGS</span></div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </ChemSectionBlock>

        {/* 9 Samples */}
        <ChemSectionBlock id="samples" n={9} title="Sampling & Chain of Custody" blurb="Auditable sample lifecycle with QR codes.">
          <Card><CardContent className="p-0">
            <table className="w-full text-xs">
              <thead className="bg-muted text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr><th className="text-left p-3">Sample ID</th><th className="text-left p-3">Type</th><th className="text-left p-3">Drawn</th><th className="text-left p-3">Witness</th><th className="text-left p-3">Status</th></tr>
              </thead>
              <tbody>
                {CHEM_SAMPLES.map(s => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="p-3 font-mono">{s.id}</td>
                    <td className="p-3">{s.type}</td>
                    <td className="p-3 text-muted-foreground">{s.drawnAt}</td>
                    <td className="p-3 text-muted-foreground">{s.witness}</td>
                    <td className="p-3"><Badge variant="outline" className="text-[10px]">{s.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
          <Button onClick={() => toast.success("Action recorded: Bulk QR Generator")} size="sm" variant="outline"><QrCode className="w-3 h-3 mr-1" />Bulk QR Generator</Button>
        </ChemSectionBlock>

        {/* 10 Vetting */}
        <ChemSectionBlock id="vetting" n={10} title="Vetting & Inspection Hub" blurb="OVMSA2, CDI-M, OVID, Flag, Class, PSC.">
          <Tabs defaultValue="ovmsa">
            <TabsList><TabsTrigger value="ovmsa">OVMSA2</TabsTrigger><TabsTrigger value="cdim">CDI-M</TabsTrigger><TabsTrigger value="psc">PSC</TabsTrigger></TabsList>
            <TabsContent value="ovmsa" className="mt-3 space-y-2">
              {CHEM_VETTING.map(r => (
                <Card key={r.vessel}><CardContent className="p-3 flex items-center justify-between text-xs">
                  <span className="font-medium">{r.vessel}</span>
                  <span className="text-muted-foreground">Last: {r.ovmsa.last} · Next: {r.ovmsa.next}</span>
                  <Badge variant="outline" className="text-[10px]">{r.ovmsa.status}</Badge>
                </CardContent></Card>
              ))}
            </TabsContent>
            <TabsContent value="cdim" className="mt-3 space-y-2">
              {CHEM_VETTING.map(r => (
                <Card key={r.vessel}><CardContent className="p-3 flex items-center justify-between text-xs">
                  <span className="font-medium">{r.vessel}</span>
                  <span className="text-muted-foreground">Last: {r.cdim.last} · Next: {r.cdim.next}</span>
                  <Badge variant="outline" className="text-[10px]">{r.cdim.status}</Badge>
                </CardContent></Card>
              ))}
            </TabsContent>
            <TabsContent value="psc" className="mt-3 text-xs text-muted-foreground">Last 12 months: 1 PSC inspection, zero deficiencies.</TabsContent>
          </Tabs>
        </ChemSectionBlock>

        {/* 11 Crew */}
        <ChemSectionBlock id="crew" n={11} title="Crew Chemical Endorsements" blurb="STCW V/1-1 advanced chemical tanker training and seatime.">
          <div className="grid md:grid-cols-3 gap-3">
            {CHEM_VESSELS.map(v => (
              <Card key={v.imo}><CardContent className="p-4">
                <p className="font-bold text-sm">{v.name}</p>
                <p className="text-xs text-muted-foreground mt-1">5 chem-endorsed officers · all current</p>
                <p className="text-xs text-muted-foreground">Avg chem seatime: 38 mo</p>
                <Badge className="mt-2 bg-emerald-100 text-emerald-700 border-0 text-[10px]">Compliant</Badge>
              </CardContent></Card>
            ))}
          </div>
        </ChemSectionBlock>

        {/* 12 Drills */}
        <ChemSectionBlock id="drills" n={12} title="Drills & Exercises" blurb="Cargo spill, H2S, tank entry rescue, IGS failure scenarios.">
          <Card><CardContent className="p-4 text-xs">
            <p className="text-muted-foreground mb-2">Last 30 days: 12 drills across fleet · 100% on-target completion</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {["Cargo Spill","H₂S Release","Tank Entry","IGS Failure"].map(s => (
                <div key={s} className="rounded-md border border-border p-2 text-center">
                  <p className="text-[10px] uppercase text-muted-foreground">{s}</p>
                  <p className="text-emerald-600 text-xs font-bold">RAG: Green</p>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </ChemSectionBlock>

        {/* 13 MARPOL */}
        <ChemSectionBlock id="marpol" n={13} title="MARPOL Annex II & Slop Management" blurb="Electronic Cargo Record Book, slop disposal logs, P&D Manual.">
          <Card><CardContent className="p-4 text-xs text-muted-foreground">
            Slop tank levels: Aurora 4.2 m³ · Triton 1.8 m³ · Borealis 12.5 m³ (disposal scheduled 2026-05-10, Port of Spain reception).
          </CardContent></Card>
        </ChemSectionBlock>

        {/* 14 Incidents */}
        <ChemSectionBlock id="incidents" n={14} title="Incident & Near-Miss Register" blurb="HSE transparency with root cause analysis.">
          <div className="grid md:grid-cols-3 gap-3">
            {[{l:"TRIR (12mo)",v:CHEM_HSE_KPIS.trir},{l:"LTIF",v:CHEM_HSE_KPIS.ltif},{l:"Spills",v:CHEM_HSE_KPIS.spills}].map(k => (
              <Card key={k.l}><CardContent className="p-4">
                <p className="text-[10px] uppercase text-muted-foreground">{k.l}</p>
                <p className="font-display text-2xl font-bold">{k.v}</p>
              </CardContent></Card>
            ))}
          </div>
        </ChemSectionBlock>

        {/* 15 Performance */}
        <ChemSectionBlock id="performance" n={15} title="Voyage Performance vs CP Warranties" blurb="Speed and consumption tracking against charter-party.">
          <Card><CardContent className="p-4 text-xs text-muted-foreground">All active voyages tracking within ±0.3 kt and ±2% fuel of CP warranty. No claim risk flags.</CardContent></Card>
        </ChemSectionBlock>

        {/* 16 CP */}
        <ChemSectionBlock id="cp" n={16} title="Charter Party Library" blurb="BIMCO templates and house riders.">
          <Card><CardContent className="p-4 text-xs">SUPPLYTIME 2017 · ASBATANKVOY · BIMCHEMVOY · Velocity Offshore house chemical rider v3.2.</CardContent></Card>
        </ChemSectionBlock>

        {/* 17 Demurrage */}
        <ChemSectionBlock id="demurrage" n={17} title="Demurrage & Laytime Workbench" blurb="Statement of Facts and auto-calculated laytime.">
          <Card><CardContent className="p-4 text-xs text-muted-foreground">3 active claims · $84k total exposure · 0 disputed.</CardContent></Card>
        </ChemSectionBlock>

        {/* 18 Supercargo */}
        <ChemSectionBlock id="supercargo" n={18} title="Supercargo & Charterer Rep Workflow" blurb="ISPS clearance, briefings, daily activity log.">
          <Card><CardContent className="p-4 text-xs text-muted-foreground">2 reps embarked across fleet · all briefings complete.</CardContent></Card>
        </ChemSectionBlock>

        {/* 19 Sustain */}
        <ChemSectionBlock id="sustain" n={19} title="Sustainability & Regulatory" blurb="CII, EEXI, EU ETS, FuelEU.">
          <div className="grid md:grid-cols-3 gap-3">
            {[{l:"Fleet CII Rating",v:"B"},{l:"EU ETS YTD",v:"$48,200"},{l:"FuelEU Balance",v:"+12%"}].map(k => (
              <Card key={k.l}><CardContent className="p-4">
                <p className="text-[10px] uppercase text-muted-foreground">{k.l}</p>
                <p className="font-display text-2xl font-bold">{k.v}</p>
              </CardContent></Card>
            ))}
          </div>
        </ChemSectionBlock>

        {/* 20 Survey */}
        <ChemSectionBlock id="survey" n={20} title="Survey & Dry-Dock Look-Ahead" blurb="24-month plan: class, BWMS, IGS major service.">
          <Card><CardContent className="p-4 text-xs text-muted-foreground">Aurora special survey window Sep 2026 · Triton intermediate Mar 2027 · Borealis BWMS commissioning Jul 2026.</CardContent></Card>
        </ChemSectionBlock>

        {/* 21 Vault */}
        <ChemSectionBlock id="vault" n={21} title="Document Vault & Q88-OSV Generator" blurb="Single source of truth and proprietary chemical PSV vetting form.">
          <Card><CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="font-bold text-sm">Q88-OSV — flagship leave-behind</p>
              <p className="text-xs text-muted-foreground">Auto-populated from live intranet data. Branded PDF for vetting submissions.</p>
            </div>
            <Button onClick={() => toast.success("Created: Generate Q88-OSV", { description: "New entry added." })}><Download className="w-4 h-4 mr-1" />Generate Q88-OSV</Button>
          </CardContent></Card>
        </ChemSectionBlock>

        {/* 22 KPI */}
        <ChemSectionBlock id="kpi" n={22} title="Audit & KPI Dashboard" blurb="Executive view across the chem fleet.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { l: "Fleet Utilization", v: "94%" },
              { l: "Open Findings", v: "2" },
              { l: "Coatings Red", v: "0" },
              { l: "Q88-OSV Freshness", v: "3d" },
            ].map(k => (
              <Card key={k.l}><CardContent className="p-4">
                <p className="text-[10px] uppercase text-muted-foreground">{k.l}</p>
                <p className="font-display text-2xl font-bold">{k.v}</p>
              </CardContent></Card>
            ))}
          </div>
        </ChemSectionBlock>
      </ChemPageShell>
    </IntranetLayout>
  );
};

export default EmployeeChemCarriage;
